import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import type { PMIssue, PMIssueDependency } from "@/domains/pm/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Translations } from "@/lib/i18n/types";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import {
  linkPreviewPath,
  linkRefusal,
  typeForSides,
  type LinkRefusal,
} from "@/domains/pm/utils/ganttDependencies";
import { createIssueDependency } from "@/stores/pm/repository";
import { useScheduleStaging } from "@/domains/pm/components/issues/schedule/scheduleStagingContext";
import { GANTT_HEADER_HEIGHT } from "./ganttConstants";
import { useEdgeAutoScroll } from "./useEdgeAutoScroll";

type Side = "start" | "end";
type GestureRefusal = LinkRefusal | "unavailable";

/** The elements the preview is drawn into. Written directly, never rendered. */
export interface LinkPreviewRefs {
  svg: React.RefObject<SVGSVGElement>;
  path: React.RefObject<SVGPathElement>;
  label: React.RefObject<HTMLDivElement>;
}

/** What the pointer is over: a bar, and which of its ends if a connector. */
interface Hit {
  issueId: number;
  side: Side | null;
  bar: HTMLElement;
  connector: HTMLElement | null;
}

interface Gesture {
  issue: PMIssue;
  side: Side;
  sourceBar: HTMLElement;
  pointer: { x: number; y: number };
  captured: { el: Element; pointerId: number } | null;
  /** What the last frame marked, so the next one can unmark it. */
  marked: { bar: HTMLElement; connector: HTMLElement | null } | null;
}

const COLOUR_OK = "#ef4129";
const COLOUR_REFUSED = "#9ca3af";

/**
 * Dragging a dependency from one bar's end to another bar.
 *
 * Deliberately the same shape as the bar drag: raw pointer events with a
 * window-level move/up pair rather than a DnD library, because the gesture
 * ends on an arbitrary pixel of another bar and there is no droppable in the
 * picture — the target is found with `elementsFromPoint`, which is also what
 * makes releasing on a bar's BODY work as well as on its connector dot.
 *
 * React is kept OUT of the pointer loop. The chart is one large observer, and
 * a state update per pointermove re-rendered every row and bar on every event
 * — which is what made the gesture judder. Now React renders twice per
 * gesture (start and end); in between, one animation frame per screen refresh
 * reads the geometry and writes the preview path, the target's highlight and
 * the cursor straight onto the DOM.
 */
export function useLinkGesture({
  buildingId,
  scrollRef,
  labelWidth,
  dependencies,
  issuesById,
  onDragActiveChange,
}: {
  buildingId: number;
  scrollRef: React.RefObject<HTMLDivElement>;
  /** The sticky title column's width — it covers the scroller's left edge. */
  labelWidth: number;
  /** Every edge on the chart, staged ones included. */
  dependencies: readonly PMIssueDependency[];
  issuesById: ReadonlyMap<number, PMIssue>;
  /** Held for the whole gesture, so a realtime reload does not re-lay the
   *  chart out from under the line being drawn. */
  onDragActiveChange?: (active: boolean) => void;
}) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const staging = useScheduleStaging();
  const autoScroll = useEdgeAutoScroll(scrollRef);
  const [active, setActive] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const svg = useRef<SVGSVGElement>(null);
  const path = useRef<SVGPathElement>(null);
  const label = useRef<HTMLDivElement>(null);
  const preview = useRef<LinkPreviewRefs>({ svg, path, label }).current;

  const gesture = useRef<Gesture | null>(null);
  const frame = useRef<number | null>(null);
  const detach = useRef<(() => void) | null>(null);

  // Everything the frame and the drop read, as of the latest committed render. The
  // listeners are attached once per gesture and must not see stale data.
  const latest = useRef({
    t,
    staging,
    dependencies,
    issuesById,
    labelWidth,
    onDragActiveChange,
    buildingId,
    queryClient,
  });
  useLayoutEffect(() => {
    latest.current = {
      t,
      staging,
      dependencies,
      issuesById,
      labelWidth,
      onDragActiveChange,
      buildingId,
      queryClient,
    };
  });

  /** Why the hovered bar cannot take the link, or null when it can. */
  const refusalFor = (from: PMIssue, targetId: number): GestureRefusal | null => {
    const to = latest.current.issuesById.get(targetId);
    if (!to) return "unavailable";
    return linkRefusal(latest.current.dependencies, from, to);
  };

  const unmark = (g: Gesture) => {
    if (!g.marked) return;
    delete g.marked.bar.dataset.linkTarget;
    if (g.marked.connector) delete g.marked.connector.dataset.linkTarget;
    g.marked = null;
  };

  /** One frame of the preview. Show the layer before measuring its origin. */
  const draw = () => {
    frame.current = null;
    const g = gesture.current;
    const layer = preview.svg.current;
    if (!g || !layer) return;
    if (!g.sourceBar.isConnected) {
      // The source row went away (a filter, a realtime removal): there is
      // nothing left to draw from.
      end();
      return;
    }

    layer.style.display = "";

    // --- reads
    const origin = layer.getBoundingClientRect();
    const source = g.sourceBar.getBoundingClientRect();
    const hit = targetUnder(g.pointer.x, g.pointer.y);
    const target = hit && hit.issueId !== g.issue.id ? hit : null;
    const targetRect = target?.bar.getBoundingClientRect() ?? null;
    const targetSide: Side = target?.side ?? "start";
    const refusal = target ? refusalFor(g.issue, target.issueId) : null;

    // --- writes
    const from = {
      x: (g.side === "end" ? source.right : source.left) - origin.left,
      y: source.top + source.height / 2 - origin.top,
      side: g.side,
    };
    const to = targetRect
      ? {
          // Snapped to the end the link would attach to, so what is drawn is
          // what a release will save.
          x: (targetSide === "end" ? targetRect.right : targetRect.left) - origin.left,
          y: targetRect.top + targetRect.height / 2 - origin.top,
          side: targetSide,
        }
      : { x: g.pointer.x - origin.left, y: g.pointer.y - origin.top, side: null };

    const colour = refusal ? COLOUR_REFUSED : COLOUR_OK;
    layer.style.color = colour;
    preview.path.current?.setAttribute("d", linkPreviewPath(from, to));
    preview.path.current?.setAttribute("stroke-dasharray", target && !refusal ? "" : "5 4");

    const tag = preview.label.current;
    if (tag) {
      tag.style.display = "";
      tag.style.transform = labelTransform(
        g.pointer.x - origin.left,
        g.pointer.y - origin.top,
        origin.width,
        origin.height
      );
      tag.style.borderColor = colour;
      tag.textContent = target
        ? refusal
          ? refusalMessage(refusal, latest.current.t)
          : latest.current.t.pm.issues.dependencies[`type${typeForSides(g.side, targetSide)}`]
        : latest.current.t.pm.issues.gantt.linkHint;
    }

    const same =
      g.marked &&
      target &&
      g.marked.bar === target.bar &&
      g.marked.connector === target.connector &&
      g.marked.bar.dataset.linkTarget === (refusal ? "refused" : "ok");
    if (!same) {
      unmark(g);
      if (target) {
        const state = refusal ? "refused" : "ok";
        target.bar.dataset.linkTarget = state;
        if (target.connector) target.connector.dataset.linkTarget = state;
        g.marked = { bar: target.bar, connector: target.connector };
      }
    }
    document.documentElement.dataset.ganttLinking = refusal ? "refused" : "ok";
  };

  const schedule = () => {
    if (frame.current === null) frame.current = requestAnimationFrame(draw);
  };

  /** Tear the gesture down, whether it was released, cancelled or lost. */
  const end = () => {
    const g = gesture.current;
    gesture.current = null;
    detach.current?.();
    autoScroll.stop();
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    if (!g) return;
    unmark(g);
    if (g.captured?.el.hasPointerCapture?.(g.captured.pointerId)) {
      g.captured.el.releasePointerCapture(g.captured.pointerId);
    }
    delete document.documentElement.dataset.ganttLinking;
    if (preview.svg.current) preview.svg.current.style.display = "none";
    if (preview.label.current) preview.label.current.style.display = "none";
    setActive(false);
    latest.current.onDragActiveChange?.(false);
  };

  const drop = async (from: PMIssue, side: Side, clientX: number, clientY: number) => {
    const target = targetUnder(clientX, clientY);
    if (!target || target.issueId === from.id) return;
    const { t: copy, staging: buffer, buildingId: building, queryClient: client } = latest.current;

    const refusal = refusalFor(from, target.issueId);
    if (refusal) {
      toast.error(refusalMessage(refusal, copy));
      return;
    }

    // Which ends were joined is what the user meant. Releasing on a bar's
    // body rather than a connector reads as its start, which makes the
    // common case — finish to start — a single left-to-right drag.
    const depType = typeForSides(side, target.side ?? "start");

    if (buffer) {
      buffer.stageEdgeAdd({
        predecessor_issue_id: from.id,
        successor_issue_id: target.issueId,
        dep_type: depType,
        lag_days: 0,
        hardness: "hard",
      });
      return;
    }

    try {
      await createIssueDependency({
        predecessor_issue_id: from.id,
        successor_issue_id: target.issueId,
        dep_type: depType,
      });
      await client.invalidateQueries({ queryKey: ["pm-issue-dependencies", building] });
    } catch (e) {
      toast.error(friendlyIssueError(e, copy));
    }
  };

  // Read through a ref by `begin`, which must keep one identity for the rows'
  // memo while still reaching the committed render's closures.
  const handlers = useRef({ draw, schedule, end, drop });
  useLayoutEffect(() => {
    handlers.current = { draw, schedule, end, drop };
  });

  /**
   * Listeners are attached HERE, inside the pointerdown, not from an effect
   * keyed on the active flag. An effect only runs after React has re-rendered,
   * so a fast flick — press, move, release inside one frame — would find
   * nothing listening and drop the gesture silently.
   */
  const begin = useCallback(
    (issue: PMIssue, side: Side, e: React.PointerEvent) => {
      handlers.current.end();
      const connector = e.currentTarget as HTMLElement;
      const sourceBar = connector.closest<HTMLElement>("button[data-gantt-bar-issue]");
      if (!sourceBar) return;

      // No text selection, no native drag of whatever is under the press.
      e.preventDefault();
      let captured: Gesture["captured"] = null;
      try {
        connector.setPointerCapture(e.pointerId);
        captured = { el: connector, pointerId: e.pointerId };
      } catch {
        // No capture: the window listeners below still see the whole gesture
        // while the pointer stays inside the page.
      }

      gesture.current = {
        issue,
        side,
        sourceBar,
        pointer: { x: e.clientX, y: e.clientY },
        captured,
        marked: null,
      };
      setActive(true);
      latest.current.onDragActiveChange?.(true);

      const move = (moveEvent: PointerEvent) => {
        const g = gesture.current;
        if (!g) return;
        g.pointer = { x: moveEvent.clientX, y: moveEvent.clientY };
        handlers.current.schedule();
      };
      const up = (upEvent: PointerEvent) => {
        const g = gesture.current;
        handlers.current.end();
        if (g) void handlers.current.drop(g.issue, g.side, upEvent.clientX, upEvent.clientY);
      };
      const cancel = () => handlers.current.end();
      const hidden = () => {
        if (document.visibilityState === "hidden") handlers.current.end();
      };
      const key = (keyEvent: KeyboardEvent) => {
        if (keyEvent.key !== "Escape") return;
        // Ours alone: the open issue's panel also closes on Escape, and one
        // press should undo one thing.
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        handlers.current.end();
      };
      const scroller = scrollRef.current;
      const scrolled = () => handlers.current.schedule();

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("blur", cancel);
      document.addEventListener("visibilitychange", hidden);
      if (captured) connector.addEventListener("lostpointercapture", cancel);
      window.addEventListener("keydown", key, true);
      scroller?.addEventListener("scroll", scrolled, { passive: true });
      detach.current = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        window.removeEventListener("blur", cancel);
        document.removeEventListener("visibilitychange", hidden);
        connector.removeEventListener("lostpointercapture", cancel);
        window.removeEventListener("keydown", key, true);
        scroller?.removeEventListener("scroll", scrolled);
        detach.current = null;
      };

      autoScroll.start({
        pointer: () => gesture.current?.pointer ?? null,
        insetTop: GANTT_HEADER_HEIGHT,
        insetLeft: latest.current.labelWidth,
        horizontal: true,
      });
      handlers.current.schedule();
    },
    [autoScroll, scrollRef]
  );

  // A gesture in flight when the chart unmounts must not leave listeners on
  // the window, a cursor override on the page, or realtime held off.
  useEffect(() => () => handlers.current.end(), []);

  const select = useCallback(
    (dependency: PMIssueDependency | null) => setSelectedId(dependency?.id ?? null),
    []
  );

  return { begin, active, selectedId, select, preview };
}

/** Room the note needs beside the cursor before it flips to the other side. */
const LABEL_ROOM_X = 240;
const LABEL_ROOM_Y = 40;

/**
 * Where the note sits: below and right of the cursor, flipped left or up near
 * the chart's far edges. An absolutely placed box is scrollable overflow, so a
 * note hanging past the content would grow the scroller — and the edge
 * auto-scroll would chase it.
 */
export function labelTransform(x: number, y: number, width: number, height: number): string {
  const left = x > width - LABEL_ROOM_X;
  const up = y > height - LABEL_ROOM_Y;
  const tx = Math.round(left ? x - 14 : x + 14);
  const ty = Math.round(up ? y - 12 : y + 16);
  return `translate(${tx}px, ${ty}px)${left ? " translateX(-100%)" : ""}${up ? " translateY(-100%)" : ""}`;
}

function refusalMessage(refusal: GestureRefusal, t: Translations): string {
  const copy = t.pm.issues.gantt;
  switch (refusal) {
    case "duplicate":
      return copy.linkRefusedDuplicate;
    case "crossProject":
      return copy.linkRefusedCrossProject;
    case "cycle":
      return copy.linkRefusedCycle;
    case "self":
      return copy.linkHint;
    case "unavailable":
      return copy.linkRefusedUnavailable;
  }
}

/** The bar under the cursor, and which of its ends if over a connector. */
function targetUnder(clientX: number, clientY: number): Hit | null {
  const stack =
    typeof document.elementsFromPoint === "function"
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)].filter((el): el is Element => !!el);
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    const id = el.dataset.ganttBarIssue;
    if (!id) continue;
    const raw = el.dataset.connectorSide;
    const side: Side | null = raw === "start" || raw === "end" ? raw : null;
    // A connector sits inside its bar's button; the body IS the button, or a
    // child of it such as the number label.
    const bar = el.closest<HTMLElement>("button[data-gantt-bar-issue]");
    if (!bar) continue;
    return { issueId: Number(id), side, bar, connector: side ? el : null };
  }
  return null;
}
